import React, { useRef, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '@godaddy/react-mintl';
import classNames from 'classnames';
import useClickAway from 'react-use/lib/useClickAway';
import Link from '@ux/link';
import AsyncIcon from '@ux/icon/async-icon.js';
import captionCodes from '../../../../public/locales/en.json';

import './AppSwitcher.css';
import { isModalOpen } from '../lib/useModalOpen';

const cleanCaptionToEidSegment = (str) => String(str).replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();

// eslint-disable-next-line complexity
function AppSwitcher({ appEid, urls, customWaffleLinks, showWaffleCommerceLink }) {
  const ref = useRef(null);
  const [appSwitcherOpen, setAppSwitcherOpen] = useState(false);
  const pageEid = appEid.extend('app_switcher_links');
  const buttonEid = useMemo(() =>
    appEid.extend('global_utility_nav')(`app_switcher.button_open_${appSwitcherOpen}.click`)
  , [appEid, appSwitcherOpen]);

  const openAppSwitcher = useCallback(() => {
    setAppSwitcherOpen(true);
  }, []);
  const closeAppSwitcher = useCallback(() => {
    setAppSwitcherOpen(false);
  }, []);

  useClickAway(ref, () => {
    if (!isModalOpen()) {
      closeAppSwitcher();
    }
  });

  const link = useCallback(({ href, eid, caption }) => {
    const derivedEidSuffix = eid || `${cleanCaptionToEidSegment(caption)}.link.click`;
    const fullEid = pageEid(derivedEidSuffix);
    return (
      captionCodes[caption] ?
        <FormattedMessage id={ caption }>
          { (text) => {
            return (
              <Link
                key={ fullEid }
                href={ href }
                data-eid={ fullEid }
                className='app-switcher-link'
                aria-label={ text }>
                { text }
              </Link>
            );
          }}
        </FormattedMessage> :
        <Link
          key={ fullEid }
          href={ href }
          data-eid={ fullEid }
          className='app-switcher-link'
          aria-label={ caption }>
          { caption }
        </Link>
    );
  }, [pageEid]);

  let topLinks = [
    link({
      eid: 'my_products.link.click',
      href: urls.products.href,
      caption: 'Shared:AppSwitcher:TopLinks:MyProducts'
    }),
    link({
      eid: 'home.link.click',
      href: urls.home.href,
      caption: 'Shared:AppSwitcher:TopLinks:ShopGD'
    })
  ];

  let quickLinks = [
    link({
      eid: 'domains.link.click',
      href: urls.manageDomains.href,
      caption: 'Shared:AppSwitcher:QuickLinks:Domains'
    }),
    link({
      eid: 'websites.link.click',
      href: urls.manageWebsites.href,
      caption: 'Shared:AppSwitcher:QuickLinks:Websites'
    }),
    link({
      eid: 'hosting.link.click',
      href: urls.manageHosting.href,
      caption: 'Shared:AppSwitcher:QuickLinks:Hosting'
    }),
    link({
      eid: 'email.link.click',
      href: urls.bigthree.email.href,
      caption: 'Shared:AppSwitcher:QuickLinks:Email'
    }),
    link({
      eid: 'ssl.link.click',
      href: urls.bigthree.ssl.href,
      caption: 'Shared:AppSwitcher:QuickLinks:SSL'
    })
  ];

  if (showWaffleCommerceLink && urls?.manageCommerce?.href) {
    const commerceLink = link({
      eid: `product_commerce.link.click`,
      href: urls.manageCommerce.href,
      caption: 'Shared:AppSwitcher:QuickLinks:CommercePayments'
    });
    quickLinks.push(commerceLink);
  }

  if (customWaffleLinks) {
    const customTopLinks = customWaffleLinks.topLinks;
    const customQuickLinks = customWaffleLinks.quickLinks;

    topLinks = customTopLinks && customTopLinks.map((topLink) => link(topLink));
    quickLinks = customQuickLinks && customQuickLinks.map((quickLink) => link(quickLink));
  }

  const hasTopAndQuickLinks = !!(topLinks && topLinks.length && quickLinks && quickLinks.length);

  return (
    <div className='app-switcher-wrapper'>
      <FormattedMessage id='Shared:AppSwitcher'>
        { (text) => (
          <button
            type='button' data-testid='app-switcher-btn'
            onClick={ openAppSwitcher }
            title={ text }
            aria-expanded={ appSwitcherOpen }
            data-eid={ buttonEid }
            className={ classNames('app-switcher-button global-nav-margin', {
              active: appSwitcherOpen
            }) }
          >
            <AsyncIcon icon='apps' />
          </button>
        )}
      </FormattedMessage>
      <div
        className={ classNames('app-switcher-component', { 'app-switcher-open': appSwitcherOpen }) }
        ref={ ref }
      >
        <div className='top-links'>
          { topLinks }
        </div>
        { hasTopAndQuickLinks && (
          <div className='app-switcher-title'>
            <FormattedMessage id='Shared:AppSwitcher:QuickLinks'/>
          </div>
        )}
        { !!(quickLinks && quickLinks.length) && (
          <div className='quick-links'>
            { quickLinks }
          </div>
        )}
      </div>
    </div>
  );
}

AppSwitcher.propTypes = {
  appEid: PropTypes.func,
  urls: PropTypes.objectOf(PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string, PropTypes.objectOf(PropTypes.string)]))).isRequired,
  customWaffleLinks: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.objectOf(PropTypes.string)
    )
  ),
  showWaffleCommerceLink: PropTypes.bool
};

export default AppSwitcher;
